import { useState } from "react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Badge } from "@/components/ui/badge";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { Search, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  SEGMENTED_TRACK,
  segmentedOptionClass,
} from "@/components/common/segmented-filter/segmented-filter-styles";
import type { ProjectPlan, ProjectTag, RecordCreator } from "@/types/api";
import { isPlanArchived } from "@/lib/records/plan-utils";
import { CreatorFilterScreen } from "@/components/common/creator-filter/creator-filter-screen";
import { creatorDisplayName } from "@/components/common/creator-filter/creator-display";
import { PlanFilterScreen } from "@/components/common/plan-filter/plan-filter-screen";
import { planDisplayName } from "@/components/common/plan-filter/plan-display";
import {
  type DateRangeChip,
  getDateRangeForChip,
  isChipActive,
} from "@/lib/records/date-range-chips";

const DATE_CHIPS: DateRangeChip[] = ["24H", "7D", "30D"];

interface RecordsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tags?: ProjectTag[];
  selectedTags?: string[];
  onTagToggle?: (tagId: string) => void;
  showUntagged?: boolean;
  onUntaggedToggle?: () => void;
  plans?: ProjectPlan[];
  selectedPlanIds?: string[];
  onPlanToggle?: (planId: string) => void;
  dateFrom?: string | null;
  dateTo?: string | null;
  onDateFromChange?: (value: string | null) => void;
  onDateToChange?: (value: string | null) => void;
  creators?: RecordCreator[];
  selectedCreatorIds?: string[];
  onCreatorToggle?: (creatorId: string) => void;
  onClearAll: () => void;
  hasActiveFilters: boolean;
}

export function RecordsDrawer({
  open,
  onOpenChange,
  tags,
  selectedTags = [],
  onTagToggle,
  showUntagged = false,
  onUntaggedToggle,
  plans,
  selectedPlanIds = [],
  onPlanToggle,
  dateFrom = null,
  dateTo = null,
  onDateFromChange,
  onDateToChange,
  creators,
  selectedCreatorIds = [],
  onCreatorToggle,
  onClearAll,
  hasActiveFilters,
}: RecordsDrawerProps) {
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [creatorScreenOpen, setCreatorScreenOpen] = useState(false);
  const [planScreenOpen, setPlanScreenOpen] = useState(false);

  const showDateSection = !!onDateFromChange && !!onDateToChange;
  const showTagsSection = !!tags && !!onTagToggle;
  // Hidden when the project has one or fewer plans — nothing to filter by.
  const showPlansSection =
    !!plans && plans.length > 1 && !!onPlanToggle;
  // Shown whenever the project has at least one creator. Hidden only on an
  // empty creators list — e.g. the public share page, which intentionally
  // passes no creators (no public endpoint to list contributors).
  const showCreatorSection =
    !!creators && creators.length > 0 && !!onCreatorToggle;

  const customActive =
    !!dateFrom &&
    !!dateTo &&
    !DATE_CHIPS.some((c) => isChipActive(c, dateFrom, dateTo));

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      setShowCustomDate(false);
      setCreatorScreenOpen(false);
      setPlanScreenOpen(false);
    }
    onOpenChange(nextOpen);
  }

  return (
    <Drawer open={open} onOpenChange={handleOpenChange}>
      <DrawerContent>
        <DrawerHeader className="pb-2">
          <div className="flex items-center justify-between">
            <DrawerTitle>Filters</DrawerTitle>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={onClearAll}
                className="text-sm text-muted-foreground underline hover:text-foreground"
              >
                Clear filter
              </button>
            )}
          </div>
        </DrawerHeader>

        <div
          className="px-4 space-y-6 max-h-[60vh] overflow-y-auto"
          style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }}
        >
          {showDateSection && (
            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                Date Range
              </h3>
              {/* One track with Custom as a fourth segment, the chosen chip raised:
                  the Issues status row's look, not solid blue pills. Pressed
                  buttons, not tabs, since tapping the chosen one clears it. See
                  [[segmented-filter.spec]]. */}
              <div role="group" aria-label="Date range" className={SEGMENTED_TRACK}>
                {DATE_CHIPS.map((chip) => {
                  // Quick chips and Custom are mutually exclusive: when the
                  // user has explicitly chosen Custom, suppress the quick-chip
                  // highlight even if the range still happens to match.
                  const active =
                    !showCustomDate && isChipActive(chip, dateFrom, dateTo);
                  return (
                    <button
                      key={chip}
                      type="button"
                      onClick={() => {
                        if (active) {
                          onDateFromChange!(null);
                          onDateToChange!(null);
                          setShowCustomDate(false);
                          return;
                        }
                        const range = getDateRangeForChip(chip);
                        onDateFromChange!(range.from);
                        onDateToChange!(range.to);
                        setShowCustomDate(false);
                      }}
                      aria-pressed={active}
                      className={cn(segmentedOptionClass(active), "flex-1 justify-center")}
                    >
                      {chip}
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={() => setShowCustomDate((v) => !v)}
                  aria-pressed={showCustomDate || customActive}
                  className={cn(
                    segmentedOptionClass(showCustomDate || customActive),
                    "flex-1 justify-center",
                  )}
                >
                  Custom
                </button>
              </div>
              {(showCustomDate || customActive) && (
                <DateRangePicker
                  inline
                  dateFrom={dateFrom}
                  dateTo={dateTo}
                  onDateFromChange={onDateFromChange!}
                  onDateToChange={onDateToChange!}
                />
              )}
            </section>
          )}

          {showCreatorSection && (
            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                Created by
              </h3>
              <button
                type="button"
                onClick={() => setCreatorScreenOpen(true)}
                aria-label="Open creator filter"
                className="flex h-9 w-full items-center gap-2 rounded-md border border-border bg-transparent px-3 text-left text-base shadow-xs transition-colors hover:bg-accent/30 md:text-sm"
              >
                <Search
                  aria-hidden="true"
                  className="h-4 w-4 shrink-0 text-muted-foreground"
                />
                <span className="truncate text-muted-foreground">
                  {selectedCreatorIds.length === 0 ? "Anyone" : "Add more…"}
                </span>
              </button>
              {selectedCreatorIds.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {selectedCreatorIds.map((id) => {
                    const creator = creators!.find((c) => c.id === id);
                    if (!creator) return null;
                    const name = creatorDisplayName(creator);
                    return (
                      <Badge
                        key={id}
                        variant="secondary"
                        role="button"
                        tabIndex={0}
                        aria-label={`Remove ${name} filter`}
                        onClick={() => onCreatorToggle!(id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onCreatorToggle!(id);
                          }
                        }}
                        className="cursor-pointer gap-1 text-sm"
                      >
                        {name}
                        <X className="h-3 w-3" />
                      </Badge>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          {showTagsSection && (
            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                Tags
              </h3>
              <div className="flex flex-wrap gap-2">
                {onUntaggedToggle && (
                  <Badge
                    variant={showUntagged ? "default" : "outline"}
                    onClick={onUntaggedToggle}
                    className={cn(
                      "cursor-pointer select-none text-sm py-1.5 px-3",
                      showUntagged && "bg-tertiary text-tertiary-foreground",
                    )}
                  >
                    Untagged
                  </Badge>
                )}
                {tags!.map((tag) => {
                  const selected = selectedTags.includes(tag.id);
                  return (
                    <Badge
                      key={tag.id}
                      variant={selected ? "default" : "outline"}
                      onClick={() => onTagToggle!(tag.id)}
                      className={cn(
                        "cursor-pointer select-none text-sm py-1.5 px-3",
                        selected && "bg-tertiary text-tertiary-foreground",
                      )}
                    >
                      {tag.name}
                    </Badge>
                  );
                })}
              </div>
            </section>
          )}

          {showPlansSection && (
            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                Floor Plans
              </h3>
              <button
                type="button"
                onClick={() => setPlanScreenOpen(true)}
                aria-label="Open plan filter"
                className="flex h-9 w-full items-center gap-2 rounded-md border border-border bg-transparent px-3 text-left text-base shadow-xs transition-colors hover:bg-accent/30 md:text-sm"
              >
                <Search
                  aria-hidden="true"
                  className="h-4 w-4 shrink-0 text-muted-foreground"
                />
                <span className="truncate text-muted-foreground">
                  {selectedPlanIds.length === 0 ? "All plans" : "Add more…"}
                </span>
              </button>
              {selectedPlanIds.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {selectedPlanIds.map((id) => {
                    const plan = plans!.find((p) => p.id === id);
                    const name = plan ? planDisplayName(plan) : id;
                    const archived = plan ? isPlanArchived(plan) : false;
                    return (
                      <Badge
                        key={id}
                        variant="secondary"
                        role="button"
                        tabIndex={0}
                        aria-label={`Remove ${name} filter`}
                        onClick={() => onPlanToggle!(id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onPlanToggle!(id);
                          }
                        }}
                        className="cursor-pointer gap-1 text-sm"
                      >
                        {name}
                        {archived ? " (Archived)" : ""}
                        <X className="h-3 w-3" />
                      </Badge>
                    );
                  })}
                </div>
              )}
            </section>
          )}

        </div>

        {showCreatorSection && (
          <CreatorFilterScreen
            open={creatorScreenOpen}
            onOpenChange={setCreatorScreenOpen}
            creators={creators!}
            selectedIds={selectedCreatorIds}
            onToggle={onCreatorToggle!}
          />
        )}
        {showPlansSection && (
          <PlanFilterScreen
            open={planScreenOpen}
            onOpenChange={setPlanScreenOpen}
            plans={plans!}
            selectedIds={selectedPlanIds}
            onToggle={onPlanToggle!}
          />
        )}
      </DrawerContent>
    </Drawer>
  );
}
